// mattermost-root-post-manager.ts — F06 (phase-07 tests 12-16):
// intent-before-POST / receipt-after delivery of a single Mattermost root or
// reply post, wired to delivery-outbox.ts. When the POST's HTTP response is
// lost (network drop after the server may have already committed the post),
// reconciles via the caller-supplied correlation-marker recovery instead of
// blind-retrying — a blind retry would create a SECOND root, which the spec
// forbids (F06). Unreconcilable sends are left `uncertain` — never silently
// promoted to `sent`, never auto-retried into a duplicate.
//
// Edit coalescing: `coalesceRootEdits` picks the highest-sequence edit out
// of a batch of pending edits to the same root, so a stale edit that arrives
// late (e.g. after a retry) never clobbers newer content — only ONE edit
// (the latest) is ever actually sent to the server.
import {
  deriveOutboxIdempotencyKey,
  findOutboxOperationByIdempotencyKey,
  type OutboxOperation,
  recordOutboxIntent,
  updateOutboxStatus,
} from "./delivery-outbox.js";

export interface RootPostSender {
  createPost: (
    channelId: string,
    message: string,
    rootId?: string,
  ) => Promise<{ id: string; message: string }>;
  editPost?: (postId: string, message: string) => Promise<{ id: string; message: string }>;
  /**
   * Search-by-correlation-marker recovery when the POST response was lost:
   * the post; `undefined` ONLY when the lookup proved it absent (a resend
   * under the same key is then safe — X1); throw when it cannot tell.
   */
  recoverByCorrelationId?: (
    correlationId: string,
  ) => Promise<{ id: string; message: string } | undefined>;
}

export interface DeliverRootPostIntentInput {
  controlRoot: string;
  channelId: string;
  eventId: string;
  missionId: string;
  sequence: number;
  message: string;
  sender: RootPostSender;
  rootId?: string;
  correlationId?: string;
  now?: () => Date;
}

export interface DeliverRootPostResult {
  status: OutboxOperation["status"];
  remoteId?: string;
}

/**
 * Records intent BEFORE calling the sender, attempts the POST, and only
 * marks the outbox operation `sent` once a remote id is confirmed — either
 * directly from the POST response, or (when the POST throws) recovered via
 * the correlation marker. A failed POST with no recovery, or no recovery
 * path at all, leaves the operation `uncertain` and returns without ever
 * calling `createPost` a second time.
 */
export async function deliverRootPostIntent(
  input: DeliverRootPostIntentInput,
): Promise<DeliverRootPostResult> {
  // B2: only an intent with a correlation id has a stable identity across
  // attempts — without one there is nothing to key on, and the op keeps the
  // pre-B2 behaviour (correlation-marker recovery below is then the only
  // duplicate protection).
  const idempotencyKey = input.correlationId
    ? deriveOutboxIdempotencyKey(input.missionId, "mattermost_post", input.correlationId)
    : undefined;
  // Review P4 M3: an op that already exists `pending` was left by a run that
  // died between the POST and the `sent` write — as uncertain as a 5xx.
  const preexisting =
    idempotencyKey !== undefined
      ? await findOutboxOperationByIdempotencyKey(input.controlRoot, idempotencyKey)
      : undefined;
  const recorded = await recordOutboxIntent({
    controlRoot: input.controlRoot,
    eventId: input.eventId,
    missionId: input.missionId,
    sequence: input.sequence,
    kind: "mattermost_post",
    payloadHash: input.eventId, // caller may pass a real content hash via eventId derivation upstream
    // P19a: persist what is needed to resend. The delivery worker only ever
    // hands `send` an event id, so without this a later tick could not
    // rebuild the message and the op would be stuck `uncertain` forever.
    payload: {
      channel_id: input.channelId,
      message: input.message,
      ...(input.rootId ? { root_id: input.rootId } : {}),
      ...(input.correlationId ? { correlation_id: input.correlationId } : {}),
    },
    ...(idempotencyKey !== undefined ? { idempotencyKey } : {}),
    now: input.now,
  });

  // B2 (+ code review 260915 H1): an intent that already reached the POST
  // stage under this key must NOT be POSTed again.
  //  - `sent`: obviously done, hand back the receipt.
  //  - `uncertain`: the request MAY already have committed a post server-side
  //    (that is exactly what `uncertain` means). `listPendingOutboxOperations`
  //    still returns it, so a later tick reaches here again — and the
  //    production sender (run-command-mattermost-transport-wiring.ts) has no
  //    `recoverByCorrelationId`, so falling through would call `createPost` a
  //    second time and duplicate the post. Reconciliation must come from a
  //    sender that can actually reconcile, never from a blind re-POST here
  //    (workflows/autopilot/recovery.md: "uncertain ⇒ hoà giải trước, không
  //    gửi mù"). X1: reconcile below first — only a lookup that PROVES the
  //    post absent lets the same key send again; otherwise it stays uncertain.
  if (recorded.status === "sent") {
    return {
      status: recorded.status,
      ...(recorded.remote_id !== undefined ? { remoteId: recorded.remote_id } : {}),
    };
  }
  // X1 (close-out 2026-09-30): an `uncertain` op is RECONCILED before any
  // send — found by its marker ⇒ `sent`; proven absent ⇒ POSTing again is
  // safe (fall through); lookup unavailable ⇒ stay `uncertain`.
  if (recorded.status === "uncertain" || preexisting?.status === "pending") {
    const found = await tryRecover(input);
    if (found === "unknown") return { status: "uncertain" };
    if (found !== undefined) {
      await updateOutboxStatus(input.controlRoot, input.eventId, "sent", found.id, input.now);
      return { status: "sent", remoteId: found.id };
    }
  }

  try {
    const post = await input.sender.createPost(input.channelId, input.message, input.rootId);
    await updateOutboxStatus(input.controlRoot, input.eventId, "sent", post.id, input.now);
    return { status: "sent", remoteId: post.id };
  } catch {
    // POST threw — response may be lost even though the server committed
    // it. Try correlation-marker recovery before ever considering a retry.
    const recovered = await tryRecover(input);
    if (recovered !== undefined && recovered !== "unknown") {
      await updateOutboxStatus(input.controlRoot, input.eventId, "sent", recovered.id, input.now);
      return { status: "sent", remoteId: recovered.id };
    }
    // Cannot reconcile — stay uncertain. NEVER call createPost again here.
    await updateOutboxStatus(input.controlRoot, input.eventId, "uncertain", undefined, input.now);
    return { status: "uncertain" };
  }
}

/**
 * Correlation-marker lookup: the post, `undefined` when the lookup ran and
 * proved it absent, or `"unknown"` when there is no lookup (no correlation id
 * / no recovery on this sender) or the lookup itself failed.
 */
async function tryRecover(
  input: DeliverRootPostIntentInput,
): Promise<{ id: string; message: string } | undefined | "unknown"> {
  if (!input.correlationId || !input.sender.recoverByCorrelationId) return "unknown";
  try {
    return await input.sender.recoverByCorrelationId(input.correlationId);
  } catch {
    return "unknown";
  }
}

export interface PendingRootEdit {
  seq: number;
  message: string;
}

/** Picks the highest-sequence edit from a batch — stale lower-seq edits coalesce away. */
export function coalesceRootEdits(pending: PendingRootEdit[]): PendingRootEdit {
  if (pending.length === 0) {
    throw new Error("coalesceRootEdits: danh sách rỗng — không có gì để coalesce.");
  }
  return pending.reduce((latest, candidate) => (candidate.seq > latest.seq ? candidate : latest));
}
